import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users as UsersIcon,
  ScrollText,
  Tag,
  CreditCard,
  ArrowUpRight,
  Plus,
  ShieldCheck,
  BookOpen,
} from "lucide-react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { PageHeader } from "@/components/PageHeader";
import { StatCard } from "@/components/StatCard";
import { CardSkeleton } from "@/components/EmptyState";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

function makeSignupSeries(users) {
  // Build 14-day signup time series from user.created_at
  const buckets = new Map();
  const now = new Date();
  for (let i = 13; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(now.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    buckets.set(key, { date: key, label: d.toLocaleDateString(undefined, { month: "short", day: "numeric" }), users: 0 });
  }
  users.forEach((u) => {
    const key = (u.created_at || "").slice(0, 10);
    if (buckets.has(key)) buckets.get(key).users += 1;
  });
  return Array.from(buckets.values());
}

function statusBreakdown(users) {
  const counts = { free: 0, active: 0, expired: 0 };
  users.forEach((u) => {
    const s = u.subscription_status || "free";
    counts[s] = (counts[s] || 0) + 1;
  });
  return [
    { name: "Free", value: counts.free, fill: "hsl(var(--chart-2))" },
    { name: "Active", value: counts.active, fill: "hsl(var(--chart-3))" },
    { name: "Expired", value: counts.expired, fill: "hsl(var(--destructive))" },
  ];
}

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ users: [], devotionals: [], types: [], settings: null });

  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([
      api.get("/users"),
      api.get("/devotionals"),
      api.get("/devotional-types"),
      api.get("/settings"),
    ]).then((results) => {
      if (cancelled) return;
      const [u, d, t, s] = results;
      setData({
        users: u.status === "fulfilled" ? u.value.data : [],
        devotionals: d.status === "fulfilled" ? d.value.data : [],
        types: t.status === "fulfilled" ? t.value.data : [],
        settings: s.status === "fulfilled" ? s.value.data : null,
      });
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const { users, devotionals, types, settings } = data;
  const activeSubs = users.filter((u) => u.subscription_status === "active").length;
  const signupSeries = makeSignupSeries(users);
  const statusSeries = statusBreakdown(users);
  const last7DaysSignups = signupSeries.slice(-7).reduce((s, x) => s + x.users, 0);
  const prev7DaysSignups = signupSeries.slice(0, 7).reduce((s, x) => s + x.users, 0);
  const signupTrend = prev7DaysSignups === 0 ? (last7DaysSignups > 0 ? 100 : 0) : Math.round(((last7DaysSignups - prev7DaysSignups) / prev7DaysSignups) * 100);

  return (
    <>
      <PageHeader
        title="Dashboard"
        description="An overview of your devotional platform — users, content and subscription activity."
        actions={
          <>
            <Button asChild variant="outline" data-testid="quick-action-add-devotional">
              <Link to="/devotionals">
                <Plus className="size-4 mr-1.5" /> New devotional
              </Link>
            </Button>
            <Button asChild data-testid="quick-action-add-admin">
              <Link to="/admin-users">
                <ShieldCheck className="size-4 mr-1.5" /> Manage admins
              </Link>
            </Button>
          </>
        }
      />

      {/* KPI cards */}
      {loading ? (
        <CardSkeleton />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            label="Total Users"
            value={users.length.toLocaleString()}
            icon={UsersIcon}
            accent="primary"
            trend={signupTrend}
            trendLabel="last 7d"
          />
          <StatCard
            label="Active Subscriptions"
            value={activeSubs.toLocaleString()}
            icon={CreditCard}
            accent="emerald"
            trend={users.length ? Math.round((activeSubs / users.length) * 100) : 0}
            trendLabel="of users"
          />
          <StatCard label="Devotionals" value={devotionals.length.toLocaleString()} icon={ScrollText} accent="amber" />
          <StatCard label="Content Types" value={types.length.toLocaleString()} icon={Tag} accent="violet" />
        </div>
      )}

      {/* Charts row */}
      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2 border-border/70 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div>
              <CardTitle className="text-base font-semibold">Sign-ups · last 14 days</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">New accounts created per day</p>
            </div>
            <Badge variant="outline" className="hidden sm:inline-flex">
              {users.length} total
            </Badge>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-[260px] shimmer rounded-lg" />
            ) : (
              <div className="h-[260px] -ml-4">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={signupSeries} margin={{ top: 8, right: 12, left: 12, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gradUsers" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{
                        background: "hsl(var(--popover))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                      labelStyle={{ color: "hsl(var(--muted-foreground))" }}
                    />
                    <Area
                      type="monotone"
                      dataKey="users"
                      stroke="hsl(var(--primary))"
                      strokeWidth={2}
                      fill="url(#gradUsers)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-border/70 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Subscription mix</CardTitle>
            <p className="text-xs text-muted-foreground">By user status</p>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-[260px] shimmer rounded-lg" />
            ) : (
              <div className="h-[260px] -ml-4">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={statusSeries} margin={{ top: 8, right: 12, left: 12, bottom: 0 }}>
                    <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{
                        background: "hsl(var(--popover))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                    />
                    <Bar dataKey="value" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent activity + quick shortcuts */}
      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2 border-border/70 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-base font-semibold">Recent users</CardTitle>
            <Button asChild variant="ghost" size="sm" data-testid="view-all-users">
              <Link to="/users">
                View all <ArrowUpRight className="size-3.5 ml-1" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent className="pt-0">
            {loading ? (
              <div className="space-y-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="h-12 shimmer rounded-lg" />
                ))}
              </div>
            ) : users.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">No users yet.</p>
            ) : (
              <ul className="divide-y divide-border">
                {users.slice(0, 5).map((u) => (
                  <li key={u.id} className="flex items-center gap-3 py-3">
                    <div className="size-9 rounded-full bg-gradient-to-br from-primary to-accent text-white flex items-center justify-center text-xs font-semibold shrink-0">
                      {(u.name || u.email || "?").slice(0, 1).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{u.name || "—"}</p>
                      <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                    </div>
                    <Badge
                      variant={u.subscription_status === "active" ? "default" : "secondary"}
                      className="capitalize shrink-0"
                    >
                      {u.subscription_status || "free"}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card className="border-border/70 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold">Quick shortcuts</CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-2">
            <QuickLink to="/devotionals" icon={ScrollText} label="Manage devotionals" hint={`${devotionals.length} published`} />
            <QuickLink to="/bible" icon={BookOpen} label="Bible books" hint="Translations & verses" />
            <QuickLink to="/system-config" icon={CreditCard} label="System Config" hint={settings ? `${settings.currency} ${settings.subscription_price}/mo` : ""} />
            <QuickLink to="/admin-users" icon={ShieldCheck} label="Admin users" hint="Roles & access" />
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function QuickLink({ to, icon: Icon, label, hint }) {
  return (
    <Link
      to={to}
      data-testid={`shortcut-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className="flex items-center gap-3 rounded-lg border border-border bg-background hover:bg-muted/60 transition-colors px-3 py-2.5 group"
    >
      <div className="size-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
        <Icon className="size-[18px]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{label}</p>
        {hint && <p className="text-xs text-muted-foreground truncate">{hint}</p>}
      </div>
      <ArrowUpRight className="size-4 text-muted-foreground group-hover:text-foreground transition-colors" />
    </Link>
  );
}
